import { useEffect, useState } from 'react'
import {
  Button,
  HStack,
  Box,
  Text,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  VStack
} from '@chakra-ui/react'
import { useStores } from 'hooks/useStores'
import set from 'lodash/set'
import { observer } from 'mobx-react'
import { FormProvider, useForm } from 'react-hook-form'
import { useNavigate, useLocation } from 'react-router-dom'
import { primary } from 'theme/globalStyles'
import { IOption } from 'types/common'
import { AuthRoleIdEnum } from 'constants/user'
import { ITheme } from 'interfaces/theme'
import routes from 'routes'
import { ISearchFilterForm } from 'stores/searchStore'
import { getValidArray } from 'utils/common'
import FilterInput from 'components/Pages/Common/FilterInput'
import Dropdown from 'components/Dropdown'
import { IDropdown } from 'types/common/select'
import { searchSortByOptions } from 'components/Topbar/constants'

const defaultValues: ISearchFilterForm = {
  groups: [],
  sort: ''
}

interface IFilterModalProps {
  isOpen: boolean
  onClose: () => void
}

const FilterModal = (props: IFilterModalProps) => {
  const { isOpen, onClose } = props
  const { organizationStore, authStore, userStore, searchStore, groupStore } = useStores()
  const { tagStore, documentTypeStore } = useStores()
  const { userDetail } = authStore
  const { organization } = organizationStore
  const { currentUserGroupMembers } = userStore
  const { searchFilter } = searchStore
  const organizationId: number = organization?.id ?? 0
  const currentTheme: ITheme = organization?.theme ?? {}
  const userGroupIds = currentUserGroupMembers.map(groupMember => groupMember.groupId)
  const isBasicUser = userDetail.authRoleId === AuthRoleIdEnum.BASIC_USER
  const navigate = useNavigate()
  const location = useLocation()
  const params = new URLSearchParams(location.search)
  const [selectedGroups, setSelectedGroups] = useState<IOption<string>[]>([])
  const [selectedDocumentTypes, setSelectedDocumentTypes] = useState<IOption<string>[]>([])
  const [selectedTags, setSelectedTags] = useState<IOption<string>[]>([])
  const [selectedSort, setSelectedSort] = useState<IDropdown | null>(null)

  const methods = useForm<ISearchFilterForm>({
    defaultValues,
    mode: 'onChange',
    resolver: async data => {
      return {
        values: data,
        errors: {}
      }
    }
  })
  const { handleSubmit, reset, setValue } = methods

  function handleSelectGroups(options: IOption<string>[]): void {
    const groups = getValidArray(options).map(option => {
      return { label: option?.label, value: Number(option?.value) }
    })
    setValue('groups', groups)
    setSelectedGroups(options)
  }

  function handleSelectDocumentTypes(options: IOption<string>[]): void {
    const documentTypes = getValidArray(options).map(option => {
      return { label: option?.label, value: Number(option?.value) }
    })
    setValue('documentTypes', documentTypes as any)
    setSelectedDocumentTypes(options)
  }

  function handleSelectTags(options: IOption<string>[]): void {
    const tags = getValidArray(options).map(option => {
      return { label: option?.label, value: Number(option?.value) }
    })
    setValue('tags', tags as any)
    setSelectedTags(options)
  }

  function handleChangeSort(name: string, dropdownValue: IDropdown): void {
    setSelectedSort(dropdownValue)
    setValue('sort', String(dropdownValue?.value ?? ''))
  }

  async function onSubmit(data: ISearchFilterForm) {
    try {
      searchStore.changeSearchFilter(data)
      params.set('page', '1')
      params.set('filter', `${JSON.stringify(data)}`)
      if (getValidArray(data?.groups)?.length > 0) {
        searchStore.changeGroupIds(getValidArray(data?.groups).map(group => Number(group.value)) || [])
      }
      onClose()
      navigate(`${routes.search.value}?${params.toString()}`)
      await searchStore.search()
    } catch (error: any) {
      console.log(error)
    }
  }

  useEffect(() => {
    if (isOpen) {
      const filterGroup = {
        where: { organizationId }
      }
      if (isBasicUser) {
        set(filterGroup, 'where.id', { inq: userGroupIds })
      }
      groupStore.getGroups(filterGroup)
      reset(searchFilter)
      setSelectedGroups(
        getValidArray(searchFilter?.groups).map(g => ({ label: String(g?.label ?? ''), value: String(g?.value ?? '') }))
      )
      setSelectedDocumentTypes(
        getValidArray(searchFilter?.documentTypes).map(d => ({ label: String(d?.label ?? ''), value: String(d?.value ?? '') }))
      )
      setSelectedTags(
        getValidArray(searchFilter?.tags).map(t => ({ label: String(t?.label ?? ''), value: String(t?.value ?? '') }))
      )
      setSelectedSort(
        searchFilter?.sort
          ? { title: String(searchFilter.sort), value: String(searchFilter.sort) }
          : null
      )
      tagStore.fetchTags({ where: { organizationId }, order: ['name ASC'] })
      documentTypeStore.fetchDocumentTypes({ where: { organizationId }, order: ['views DESC'], include: ['iconBuilder'] })
    }
  }, [isOpen])

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <FormProvider {...methods}>
        <form onSubmit={handleSubmit(onSubmit)} id="search-form">
          <ModalOverlay />
          <ModalContent minWidth={{ base: 'full', md: '800px' }} borderRadius={8}>
            <ModalHeader fontSize="lg" fontWeight={500} lineHeight={7} color="gray.800">
              More filters
            </ModalHeader>
            <ModalCloseButton background="white" border="none" boxShadow="none !important" />
            <ModalBody border="1px solid #E2E8F0" padding={6}>
            <Box display={{ base: 'block', md: 'none' }} >
                <Box width="full">
                  <Text color="gray.700" fontSize="16px" fontWeight={500} mb={2}>
                    Sort By
                  </Text>
                  <Dropdown
                    name="sort"
                    titleDropdown="SORT BY"
                    fontSize="md"
                    width={{ base: 'full' }}
                    height="40px"
                    options={searchSortByOptions}
                    item={{
                      title: selectedSort?.title ?? (searchFilter.sort ? String(searchFilter.sort) : 'Most relevant'),
                      value: selectedSort?.value ?? (searchFilter.sort ? String(searchFilter.sort) : 'Most relevant')
                    }}
                    setValue={handleChangeSort}
                    isDisabled={false}
                    isMatchWidth={true}
                  />
                </Box>
                <Box borderTop="1px solid #E2E8F0" mt={6} mb={6} />
                <VStack width="full" gap={0} alignItems="flex-start">
                  <FilterInput
                    isOpenModal={isOpen}
                    name="documentTypes"
                    label="Document Type"
                    placeholder="Search document types"
                    storeOptions={getValidArray(documentTypeStore.documentTypes)}
                    filteredOptions={getValidArray(searchFilter?.documentTypes)}
                    selectedOptions={selectedDocumentTypes}
                    setSelectedOptions={handleSelectDocumentTypes}
                  />           
                  <FilterInput
                    isOpenModal={isOpen}
                    name="tags"
                    label="Tag"
                    placeholder="Search tags"
                    storeOptions={getValidArray(tagStore.tags)}
                    filteredOptions={getValidArray(searchFilter?.tags)}
                    selectedOptions={selectedTags}
                    setSelectedOptions={handleSelectTags}
                  />
                </VStack>
              </Box>
              <HStack width="full" alignItems="flex-start" spacing={6}>
                <FilterInput
                  isOpenModal={isOpen}
                  name="groups"
                  label="Group"
                  placeholder="Search groups by name"
                  storeOptions={getValidArray(groupStore.groups)}
                  filteredOptions={getValidArray(searchFilter?.groups)}
                  selectedOptions={selectedGroups}
                  setSelectedOptions={handleSelectGroups}
                />
              </HStack>
            </ModalBody>
            <ModalFooter>
              <HStack width="full" justify="space-between" alignItems="center">
                <Text
                  color="gray.700"
                  fontSize="16px"
                  fontWeight={500}
                  lineHeight={6}
                  cursor="pointer"
                  display={{ base: 'block', md: 'none' }}
                  onClick={() => {
                    searchStore.clearSearchFilter()
                    setSelectedGroups([])
                    setSelectedDocumentTypes([])
                    setSelectedTags([])
                    setSelectedSort(null)
                    reset({ ...searchFilter, groups: [], documentTypes: [] as any, tags: [] as any, sort: '' })
                  }}
                >
                  Clear filters
                </Text>
                <HStack>
                  <Button variant="outline" background="white" fontWeight={500} lineHeight={6} onClick={onClose}>
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    form="search-form"
                    variant="outline"
                    color="white"
                    fontWeight={500}
                    lineHeight={6}
                    background={currentTheme?.primaryColor ?? primary}
                    _hover={{ opacity: 0.8 }}
                  >
                    Apply
                  </Button>
                </HStack>
              </HStack>
            </ModalFooter>
          </ModalContent>
        </form>
      </FormProvider>
    </Modal>
  )
}

export default observer(FilterModal)
